import c from 'classnames';
import React from 'react';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';

import { Typography } from 'components';
import styles from './Maturation.module.css';
import slideStyles from './Slides.module.css';

import { EvaluationColor } from '@repo/types';
import BACKGROUND_GRAY from 'assets/images/summary/first-look/maturation_background_gray.svg';
import BACKGROUND_GREEN from 'assets/images/summary/first-look/maturation_background_green.svg';
import BACKGROUND_ORANGE from 'assets/images/summary/first-look/maturation_background_orange.svg';
import BACKGROUND_RED from 'assets/images/summary/first-look/maturation_background_red.svg';
import { AgeUnit, getAgeDivision, stringifyDaysOld } from 'helpers';
import useSubMetricEvaluations from 'services/insights/useSubMetricEvaluations';

export const MATURATION_INDEX_CATEGORY_KEY = 'Maturation';

function getConfig(color: EvaluationColor) {
  switch (color) {
    case 'green':
      return {
        style: styles.green,
        background: BACKGROUND_GREEN,
        label: 'Good',
      };
    case 'orange':
      return {
        style: styles.orange,
        background: BACKGROUND_ORANGE,
        label: 'Not ideal',
      };
    case 'red':
      return {
        style: styles.red,
        background: BACKGROUND_RED,
        label: 'Not ideal',
      };
    default:
    case 'grey':
      return {
        style: styles.gray,
        background: BACKGROUND_GRAY,
        label: 'Okay',
      };
  }
}

function getUnitLabel(unit: AgeUnit) {
  switch (unit) {
    case 'year':
      return 'year old';
    case 'month':
      return 'month old';
    case 'week':
      return 'week old';
  }
}

export const Maturation: React.FC = () => {
  const { currentKit, currentTinyAccount } = useCurrentKitAndTinyAccount();
  const { data: metrics } = useSubMetricEvaluations();

  const maturationIndexSubMetric = metrics.find(
    subMetric => subMetric.category_key === MATURATION_INDEX_CATEGORY_KEY,
  );

  if (!currentKit || !currentTinyAccount || !maturationIndexSubMetric)
    return <></>;

  const gutAge = getAgeDivision(maturationIndexSubMetric.user_value);
  const config = getConfig(maturationIndexSubMetric.evaluation_color);

  return (
    <div
      className={c(slideStyles.slideWrapper, styles.container, config.style)}
    >
      <img
        src={config.background}
        alt='maturation index background'
        className={styles.background}
        height={543}
        width={543}
      />

      <Typography variant='label' weight='light' className={styles.title}>
        Microbiome Maturation Index
      </Typography>
      <Typography variant='heading-l' className={styles.subtitle}>
        At{' '}
        <Typography variant='heading-l' weight='bold'>
          {stringifyDaysOld(currentKit.age_at_sampling || 0)}
        </Typography>
        , {currentTinyAccount.first_name}'s gut looks like that of a
      </Typography>

      <Typography variant='heading-l' className={styles.score}>
        {gutAge.amount}
      </Typography>
      <Typography variant='label' className={styles.scoreLabel}>
        {getUnitLabel(gutAge.unit)}
      </Typography>

      <Typography variant='label' className={styles.scorePill}>
        {config.label}
      </Typography>
    </div>
  );
};
